// DAS-3257: Alpha card logic — turns the per-ticker signals (GET /api/alpha_card/<ticker>,
// DAS-3256) and the register (GET /api/alpha_register, DAS-3157) into the card's lines and
// its headline. Pure, no fetch: the stock-page block (sibling ticket) calls this with both
// responses. The headline is always the single best carried positive edge, never a sum
// (DAS-3255 explains why summing register rows is wrong).

const ORDINAL_SUFFIX = ['th', 'st', 'nd', 'rd'];

function ordinal(n) {
  if (n == null) return null;
  const v = Math.abs(Math.round(n)) % 100;
  const suffix = ORDINAL_SUFFIX[(v - 20) % 10] || ORDINAL_SUFFIX[v] || ORDINAL_SUFFIX[0];
  return `${Math.round(n)}${suffix}`;
}

function humanizeHorizon(h) {
  if (!h) return null;
  const m = /^(\d+)m$/.exec(h);
  return m ? `${m[1]} months` : h;
}

function humanizeRegimeCondition(cond) {
  if (cond === 'NEUTRAL_OR_BEAR') return 'a NEUTRAL or BEAR month';
  if (!cond) return 'a different month';
  return `a ${cond} month`;
}

function registerFor(entry, registerByKey) {
  return registerByKey.get(entry.signal_key) || null;
}

function edgeOf(entry, registerRow) {
  const v = entry.edge_eur_per_10k != null ? entry.edge_eur_per_10k : registerRow && registerRow.edge_eur_per_10k;
  return v != null ? Number(v) : null;
}

function horizonOf(entry, registerRow) {
  return entry.edge_horizon || (registerRow && registerRow.edge_horizon) || null;
}

function comparedWithOf(entry, registerRow) {
  return entry.compared_with || (registerRow && registerRow.compared_with) || null;
}

function regimeConditionOf(entry, registerRow) {
  return entry.regime_condition || (registerRow && registerRow.regime_condition) || null;
}

function regimeConditionMet(entry) {
  const v = entry.value;
  return !!(v && v.regime_condition_met === true);
}

function regimeConditionUnmet(entry) {
  const v = entry.value;
  return !!(v && v.regime_condition_met === false);
}

function formatValueText(entry) {
  const v = entry.value;
  switch (entry.signal_key) {
    case 'good_fund_buyers':
      return v == null ? null : `good-fund buyers: ${v}, quarter ${entry.date || 'unknown'}`;
    case 'zacks_value_a':
      return v == null ? null : `Zacks Value: ${v}`;
    case 'cheap_pe_sector':
    case 'small_cheap_pe':
    case 'cheap_pe_after_stress': {
      if (!v || v.pe == null || v.rank == null) return null;
      const base = `P/E ${Math.round(v.pe)}, ${ordinal(v.rank)} in ${v.sector || 'its sector'}`;
      if (entry.signal_key === 'cheap_pe_after_stress') return base;
      return `${base}: ${entry.carries ? 'in the cheapest fifth' : 'not in the cheapest fifth'}`;
    }
    case 'earnings_beat':
    case 'earnings_miss': {
      if (v == null) return null;
      const sign = v > 0 ? '+' : '';
      return `earnings surprise ${sign}${v}%, reported ${entry.date || 'unknown'}`;
    }
    default:
      return v == null ? null : String(v);
  }
}

function buildNote(entry, registerRow) {
  const edge = edgeOf(entry, registerRow);
  if (entry.carries === true && edge != null && edge < 0) {
    return 'carries today — this signal has a negative measured edge, a warning, not an opportunity';
  }
  const cond = regimeConditionOf(entry, registerRow);
  if (cond && regimeConditionUnmet(entry)) {
    const lastMonth = entry.value && entry.value.regime_prev_month;
    return `needs ${humanizeRegimeCondition(cond)} before; last month was ${lastMonth || 'unknown'}`;
  }
  return null;
}

function buildEdgeText(entry, registerRow) {
  const edge = edgeOf(entry, registerRow);
  if (edge == null) return null;
  const horizon = humanizeHorizon(horizonOf(entry, registerRow));
  const comparedWith = comparedWithOf(entry, registerRow);
  const amount = `€${Math.abs(Math.round(edge))}`;
  let text = edge < 0 ? `a loss of ${amount} per €10,000` : `about ${amount} per €10,000`;
  if (horizon) text += ` over ${horizon}`;
  if (comparedWith) text += `, vs ${comparedWith}`;
  return text;
}

function buildLine(entry, registerByKey) {
  const registerRow = registerFor(entry, registerByKey);
  const label = (registerRow && registerRow.name) || entry.name || entry.signal_key;
  const valueText = entry.carries === null
    ? (entry.reason || 'no data for this stock')
    : (formatValueText(entry) || entry.reason || 'no data for this stock');

  return {
    key: entry.signal_key,
    label,
    carries: entry.carries,
    valueText,
    edgeText: buildEdgeText(entry, registerRow),
    note: buildNote(entry, registerRow),
  };
}

function isHeadlineCandidate(entry, registerRow) {
  if (entry.carries !== true) return false;
  const edge = edgeOf(entry, registerRow);
  if (edge == null || edge <= 0) return false;
  const cond = regimeConditionOf(entry, registerRow);
  if (cond) return regimeConditionMet(entry);
  return true;
}

function buildHeadline(signals, registerByKey) {
  const candidates = signals
    .map((entry) => ({ entry, registerRow: registerFor(entry, registerByKey) }))
    .filter(({ entry, registerRow }) => isHeadlineCandidate(entry, registerRow));

  if (!candidates.length) return 'No proven signal today';

  const best = candidates.reduce((a, b) => (edgeOf(b.entry, b.registerRow) > edgeOf(a.entry, a.registerRow) ? b : a));
  const { entry, registerRow } = best;
  const label = (registerRow && registerRow.name) || entry.name || entry.signal_key;
  const amount = Math.round(edgeOf(entry, registerRow));
  const horizon = humanizeHorizon(horizonOf(entry, registerRow));
  return `Expected edge: about €${amount} per €10,000${horizon ? ` over ${horizon}` : ''}, from ${label} (best single signal, not a sum)`;
}

export function buildAlphaCard(cardResponse, registerRows) {
  const signals = (cardResponse && cardResponse.signals) || [];
  const registerByKey = new Map(
    (registerRows || []).filter((r) => r && r.signal_key).map((r) => [r.signal_key, r]),
  );

  return {
    headline: buildHeadline(signals, registerByKey),
    lines: signals.map((entry) => buildLine(entry, registerByKey)),
  };
}

// DAS-3298: the one-cell summary for the portfolio sleeve view. Pure. Reads the stored card
// (Ticker_Performance_Data.alpha_card_json, a JSON string or an object) off a sleeve row and
// returns { state, amount, label, horizon, date, lines }:
//   state 'signal'  -> amount = the LARGEST single carried edge in EUR per 10,000 (never a sum), label, horizon
//   state 'none'    -> a card, but no carried signal with a positive edge
//   state 'unknown' -> no stored card (new ticker, funds)
export function bestSignal(cardResponse, registerRows) {
  const signals = (cardResponse && cardResponse.signals) || [];
  const registerByKey = new Map(
    (registerRows || []).filter((r) => r && r.signal_key).map((r) => [r.signal_key, r]),
  );
  let best = null;
  signals.forEach((entry) => {
    const registerRow = registerFor(entry, registerByKey);
    if (!isHeadlineCandidate(entry, registerRow)) return;
    const amount = edgeOf(entry, registerRow);
    if (!best || amount > best.amount) {
      best = {
        key: entry.signal_key,
        label: (registerRow && registerRow.name) || entry.name || entry.signal_key,
        amount,
        horizon: humanizeHorizon(horizonOf(entry, registerRow)),
      };
    }
  });
  return best;
}

export function sleeveAlpha(record, registerRows) {
  if (!record) return { state: 'unknown', amount: null, label: null, horizon: null, date: null, lines: [] };
  let card = record.alpha_card_json;
  if (typeof card === 'string') {
    try { card = JSON.parse(card); } catch (e) { card = null; }
  }
  const date = record.alpha_card_date ? String(record.alpha_card_date).slice(0, 10) : null;
  if (!card || !Array.isArray(card.signals)) {
    return { state: 'unknown', amount: null, label: null, horizon: null, date, lines: [] };
  }
  const best = bestSignal(card, registerRows);
  const lines = buildAlphaCard(card, registerRows).lines;
  if (!best) return { state: 'none', amount: 0, label: null, horizon: null, date, lines };
  return { state: 'signal', amount: Math.round(best.amount), label: best.label, horizon: best.horizon, date, lines };
}

export default buildAlphaCard;
